// Copyright (c) 2021, DIALPAD INC., all rights reserved.

import React, {Fragment} from 'react';
import {reset} from 'redux-form';
import {connect} from 'react-redux';
import {Link, withRouter} from 'react-router-dom';

import {
    AccountCircle,
    Assessment,
    Document,
    Improve,
    Inbox,
    OptionsTooltip,
    PlayArrow,
    Automation,
    HelpOutline,
} from '@app/common/icons';
import {Tooltip} from '@app/common';
import {redux} from '@app/redux';
import {permissions} from '@app/utils';
import {PERMISSION_KEYS} from '@app/constants';
import {NavLink} from './nav-link';
import {LanguagePicker} from './language-picker';
import logo from './dialpad-self_service-logo.svg';
import {AccountMenu} from './account-menu';
import {RESPONSE_FORM} from '@app/responses/constants';
import {GET_ATTACHED_DOCUMENT_FIELD_DOCUMENT} from '@app/redux/documents/constants';
import {MdExpandMore, MdSupervisorAccount} from 'react-icons/md';
import {BetaPreview} from './beta-preview-button';
import {getSubdomainFromURL} from '@app/utils/client-url';
import {hasScope} from '@app/utils/auth';

interface NavigationComponentProps {
    history?: any;
    location?: any;
    resetResponseForm?: any;
    applicationId?: string;
    scopes?: any[];
}

const NAVLINK_KEYS = {
    DASHBOARD: 'DASHBOARD',
    IMPROVE: 'IMPROVE',
    ACTIVITY: 'ACTIVITY',
    KNOWLEDGE: 'KNOWLEDGE',
    AUDIENCE: 'AUDIENCE',
};

export class NavigationComponent extends React.Component<NavigationComponentProps> {
    state = {
        showImproveDropdown: false,
        showActivityDropdown: false,
        activeDropdown: null,
    };

    getWidgetTestLink = () => {
        const {applicationId} = this.props;
        return `/widget/${applicationId}`;
    };

    handleClickWidgetLauncher = () => {
        //@ts-ignore
        window.kare && window.kare.open();
    };

    setActiveDropdown() {
        const {pathname} = this.props.location;
        if (pathname.includes('/bi')) {
            if (this.state.activeDropdown !== NAVLINK_KEYS.DASHBOARD) {
                this.setState({activeDropdown: NAVLINK_KEYS.DASHBOARD});
            }
        } else if (
            pathname.includes('/improve') ||
            pathname.includes('/consolidate') ||
            pathname.includes('/expand')
        ) {
            if (this.state.activeDropdown !== NAVLINK_KEYS.IMPROVE) {
                this.setState({activeDropdown: NAVLINK_KEYS.IMPROVE});
            }
        } else if (
            pathname.includes('/responses') ||
            pathname.includes('/library') ||
            pathname.includes('/chattering')
        ) {
            if (this.state.activeDropdown !== NAVLINK_KEYS.KNOWLEDGE) {
                this.setState({activeDropdown: NAVLINK_KEYS.KNOWLEDGE});
            }
        } else if (pathname.includes('/audience/default')) {
            if (this.state.activeDropdown !== NAVLINK_KEYS.AUDIENCE) {
                this.setState({activeDropdown: NAVLINK_KEYS.AUDIENCE});
            }
        } else if (
            pathname.includes('/activity/conversations') ||
            pathname.includes('/activity/feedback')
        ) {
            if (this.state.activeDropdown !== NAVLINK_KEYS.ACTIVITY) {
                this.setState({activeDropdown: NAVLINK_KEYS.ACTIVITY});
            }
        }
    }

    componentDidMount() {
        this.setActiveDropdown();
    }

    UNSAFE_componentWillUpdate() {
        this.setActiveDropdown();
    }

    render() {
        const {applicationId, scopes} = this.props;
        const {activeDropdown} = this.state;
        const iconsSize = 20;
        const expandIconSize = 25;
        const hasKnowledgePermission = permissions.hasRequiredPermissions(
            [
                PERMISSION_KEYS.MIND.KNOWLEDGE.READ,
                PERMISSION_KEYS.MIND.KNOWLEDGE.WRITE,
            ],
            scopes,
        );
        const hasKnowledgeReadPermission = permissions.hasRequiredPermissions(
            [PERMISSION_KEYS.MIND.KNOWLEDGE.READ],
            scopes,
        );
        const hasSettingsPermission = hasScope(
            scopes,
            PERMISSION_KEYS.MIND.SETTINGS,
        );

        return (
            <section className="kare-side-bar">
                <nav>
                    <Link to="/bi/kpis" className="dialpad-sidebar-logo">
                        <img src={logo} alt="Dialpad logo" />
                    </Link>
                    {hasKnowledgeReadPermission && (
                        <Fragment>
                            {!hasKnowledgePermission && (
                                <NavLink
                                    to="/bi/kpis"
                                    activeRoutes={['/bi/kpis']}
                                >
                                    <Assessment size={iconsSize} />
                                    <span>KPIS</span>
                                </NavLink>
                            )}
                            {hasKnowledgePermission && (
                                <div>
                                    <NavLink
                                        to="/bi/kpis"
                                        activeRoutes={['/bi']}
                                    >
                                        <Assessment size={iconsSize} />
                                        <span>Dashboard</span>
                                        <MdExpandMore
                                            className="kare-side-bar__dropdown"
                                            size={expandIconSize}
                                        />
                                    </NavLink>
                                    {activeDropdown ===
                                        NAVLINK_KEYS.DASHBOARD && (
                                        <div className="kare-side-bar__submenu-5">
                                            <NavLink
                                                to="/bi/kpis"
                                                activeRoutes={['/bi/kpis']}
                                            >
                                                <span>KPIs</span>
                                            </NavLink>
                                            <NavLink
                                                to="/bi/engagement"
                                                activeRoutes={[
                                                    '/bi/engagement',
                                                    '/bi/beacons',
                                                ]}
                                            >
                                                <span>engagement</span>
                                            </NavLink>{' '}
                                            <NavLink
                                                to="/bi/completion"
                                                activeRoutes={[
                                                    '/bi/completion',
                                                    '/bi/click',
                                                    '/bi/automation',
                                                ]}
                                            >
                                                <span>completion</span>
                                            </NavLink>{' '}
                                            <NavLink
                                                to="/bi/knowledge"
                                                activeRoutes={[
                                                    '/bi/knowledge',
                                                    '/bi/performance',
                                                ]}
                                            >
                                                <span>knowledge</span>
                                            </NavLink>
                                        </div>
                                    )}
                                    <NavLink
                                        to="/expand"
                                        activeRoutes={[
                                            '/improve',
                                            '/consolidate',
                                            '/expand',
                                        ]}
                                        onClick={() => {
                                            this.setState({
                                                showImproveDropdown: true,
                                            });
                                        }}
                                    >
                                        <Improve size={iconsSize} />
                                        <span>Improve</span>
                                        <MdExpandMore
                                            className="kare-side-bar__dropdown"
                                            size={expandIconSize}
                                        />
                                    </NavLink>
                                    {activeDropdown ===
                                        NAVLINK_KEYS.IMPROVE && (
                                        <div className="kare-side-bar__submenu-2">
                                            <NavLink
                                                to="/expand"
                                                activeRoutes={['/improve']}
                                            >
                                                <span>Expand</span>
                                            </NavLink>
                                            <NavLink
                                                to="/consolidate"
                                                activeRoutes={['/consolidate']}
                                            >
                                                <span>Consolidate</span>
                                            </NavLink>
                                        </div>
                                    )}
                                </div>
                            )}
                            <NavLink
                                to="/activity/conversations"
                                activeRoutes={[
                                    '/activity/conversations',
                                    '/activity/feedback',
                                ]}
                                onClick={() => {
                                    this.setState({
                                        showActivityDropdown: true,
                                    });
                                }}
                            >
                                <Inbox size={iconsSize} />
                                <span>Activity</span>
                                <MdExpandMore
                                    className="kare-side-bar__dropdown"
                                    size={expandIconSize}
                                />
                            </NavLink>
                            {activeDropdown === NAVLINK_KEYS.ACTIVITY && (
                                <div className="kare-side-bar__submenu-2">
                                    <NavLink
                                        to="/activity/conversations"
                                        activeRoutes={[
                                            '/activity/conversations',
                                        ]}
                                    >
                                        <span>Conversations</span>
                                    </NavLink>
                                    <NavLink
                                        to="/activity/feedback"
                                        activeRoutes={['/activity/feedback']}
                                    >
                                        <span>Feedback</span>
                                    </NavLink>
                                </div>
                            )}
                        </Fragment>
                    )}
                    {hasSettingsPermission && (
                        <NavLink
                            to="/actions"
                            exact={true}
                            activeRoutes={['/actions']}
                        >
                            <Automation size={iconsSize} />
                            <span>automations</span>
                        </NavLink>
                    )}
                    {hasKnowledgePermission && (
                        <Fragment>
                            <NavLink
                                to="/responses"
                                activeRoutes={[
                                    '/responses',
                                    '/library',
                                    '/chattering',
                                ]}
                                onClick={() => {
                                    this.setState({
                                        showImproveDropdown: true,
                                    });
                                }}
                            >
                                <Document size={iconsSize} />

                                <span>Knowledge</span>
                                <MdExpandMore
                                    className="kare-side-bar__dropdown"
                                    size={expandIconSize}
                                />
                            </NavLink>

                            {activeDropdown === NAVLINK_KEYS.KNOWLEDGE && (
                                <div className="kare-side-bar__submenu-3">
                                    <NavLink
                                        to="/responses"
                                        exact={true}
                                        activeRoutes={['/responses']}
                                    >
                                        <span>Responses</span>
                                    </NavLink>
                                    <NavLink
                                        to="/library"
                                        exact={true}
                                        activeRoutes={['/library']}
                                    >
                                        <span>Documents</span>
                                    </NavLink>
                                    <NavLink
                                        to="/chattering"
                                        exact={true}
                                        activeRoutes={['/chattering']}
                                    >
                                        <span>Chatter</span>
                                    </NavLink>
                                </div>
                            )}
                        </Fragment>
                    )}{' '}
                    {hasKnowledgePermission && (
                        <Fragment>
                            <NavLink
                                to="/audience/default"
                                activeRoutes={['/audience/default']}
                                onClick={() => {
                                    this.setState({
                                        showAudienceDropdown: true,
                                    });
                                }}
                            >
                                <MdSupervisorAccount size={iconsSize} />

                                <span>Audience</span>
                                <MdExpandMore
                                    className="kare-side-bar__dropdown"
                                    size={expandIconSize}
                                />
                            </NavLink>

                            {activeDropdown === NAVLINK_KEYS.AUDIENCE && (
                                <div className="kare-side-bar__submenu-2">
                                    <NavLink
                                        to="/audience/default"
                                        exact={true}
                                        activeRoutes={['/audience/default']}
                                    >
                                        <span>Default</span>
                                    </NavLink>
                                </div>
                            )}
                        </Fragment>
                    )}
                    <div className="kare-nav-bottom-section">
                        <BetaPreview subdomain={getSubdomainFromURL()} />
                        {applicationId && (
                            <a
                                target="_blank"
                                className="kare-nav-link"
                                rel="noreferrer"
                                href={this.getWidgetTestLink()}
                            >
                                <PlayArrow size={iconsSize} />
                                <span>Try now</span>
                            </a>
                        )}
                        <div
                            className="kare-nav-link"
                            onClick={this.handleClickWidgetLauncher}
                        >
                            <HelpOutline size={iconsSize} />
                            <span>Help</span>
                        </div>
                        <Tooltip
                            overlay={<AccountMenu />}
                            tooltipProps={{
                                placement: 'top',
                            }}
                            isSimple={true}
                        >
                            <div className="kare-nav-link kare-nav-link--account">
                                <AccountCircle size={iconsSize} />
                                <span>Account</span>

                                <OptionsTooltip
                                    size={20}
                                    className="more-vertical"
                                />
                            </div>
                        </Tooltip>
                        <LanguagePicker />
                    </div>
                    <Link
                        to="/copyright"
                        target="_blank"
                        className="kare-side-bar-patent"
                    >
                        <span>Patented technology</span>
                    </Link>
                </nav>
            </section>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    resetResponseForm: () => {
        dispatch(
            redux.rest.actions[GET_ATTACHED_DOCUMENT_FIELD_DOCUMENT].reset(),
        );
        dispatch(reset(RESPONSE_FORM));
    },
    getWidget: (id) => {
        dispatch(redux.rest.actions[redux.widget.constants.GET_WIDGET]({id}));
    },
});

const mapStateToProps = (state) => {
    return {
        applicationId: redux.application.selectors.applicationIdSelector(state),
        scopes: redux.user.selectors.scopesSelector(state),
    };
};
export const Navigation = withRouter(
    connect(mapStateToProps, mapDispatchToProps)(NavigationComponent),
);
